import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import { PageNotFound } from '@risksmart-app/components/src/errors/errors';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { Parent_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { useObjectToolbarFormStore } from 'src/blocks/object-toolbar/useObjectToolbarFormStore';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal/DeleteModal';
import TabSettingsModal from 'src/components/tab-settings-modal';
import { useDeleteControlGroup } from 'src/hooks/mutations';
import {
  useGetControlGroupById,
  useGetControlGroupsRegister,
} from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';
import { useGetDetailPath } from 'src/routes/useGetDetailParentPath';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import useTabs from '@/hooks/useTabs';

type Props = {
  activeTabId: 'controls' | 'details' | 'linkedItems' | 'notificationHistory';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const controlGroupId = useGetGuidParam('controlGroupId');
  const navigate = useNavigate();
  const detailsPath = useGetDetailPath(controlGroupId);

  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [showTabSettings, setShowTabSettings] = useState(false);
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'controlGroups',
  });
  const defaultTitle = st('fallback_title', 'Control Group');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  // Form-editor opener bridged up from the active tab's FormMenu (relocate mode).
  // control-groups has no FormMenu today, so this stays null and the edit-form
  // action is omitted — the conditional is kept for parity with the pilot.
  const formConfig = useObjectToolbarFormStore((s) => s.formConfigState);

  const { data, error } = useGetControlGroupById({
    queryArgs: { controlGroupId },
  });
  const { refetch } = useGetControlGroupsRegister({ queryArgs: {} });

  if (error) {
    throw error;
  }
  const controlGroup = data?.control_group?.[0];

  const { deleteControlGroup, loading } = useDeleteControlGroup();

  const onDelete = useDeleteResultNotification({
    asyncAction: async () => {
      if (!controlGroup) {
        throw new Error('Missing control group');
      }
      await deleteControlGroup({
        id: controlGroup.Id,
        modifiedAtTimestamp: controlGroup.ModifiedAtTimestamp,
      });

      await refetch();
      navigate(`/control-groups`);

      return true;
    },
    entityName: st('entity_name', 'control group'),
  });

  if (data?.control_group.length === 0) {
    throw new PageNotFound(`Control group with id ${controlGroupId} not found`);
  }
  const tabs = useTabs({
    parentType: Parent_Type_Enum.ControlGroup,
    parent: controlGroup,
    hrefRoot: detailsPath,
  });

  // Object-level toolbar (CUS-153) — flag-gated. Relocates Delete into the
  // toolbar's Danger zone, hosts the "Edit tabs" overflow action, and surfaces
  // the Save disc (state bridged from the details tab's form). No export action —
  // control-groups has no detail-page export today (not adding one). No
  // `parentObject`: the legacy delete gate was type-level (<Permission> with no
  // parentObject), so the delete permission resolves type-level too, preserving
  // parity — control-groups uses type-level permissions throughout.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: {
        title: controlGroup?.Title ?? defaultTitle,
      },
      save: true,
      actions: {
        overflow: [
          {
            id: 'edit',
            label: t('edit'),
            actions: [
              ...(formConfig
                ? [
                    {
                      id: 'edit-form',
                      label: t('edit_form'),
                      iconName: 'file-06' as const,
                      onClick: formConfig.openConfigure,
                    },
                  ]
                : []),
              {
                id: 'edit-tabs',
                label: t('edit_tabs'),
                iconName: 'layers-three-01',
                onClick: () => setShowTabSettings(true),
              },
            ],
          },
        ],
        danger: [
          {
            id: 'delete',
            label: st('delete_button', 'Delete'),
            iconName: 'trash-01',
            permission: 'delete:control_group',
            // Wait for the entity to hydrate. `onDelete` throws
            // `Missing control group` when `controlGroup` is absent, which
            // `mutationResultNotification` surfaces as a bare "Failed to save
            // changes" with NO request issued, so a click inside the hydration
            // window looks like a broken delete (ENG-616). Disabling also
            // restores Playwright's actionability wait.
            disabled: !controlGroup,
            onClick: () => setIsDeleteModalVisible(true),
          },
        ],
      },
    }),
    [controlGroup, defaultTitle, t, st, formConfig]
  );

  return (
    <PageLayout
      title={controlGroup?.Title}
      meta={{
        title: defaultTitle,
      }}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <Permission permission={'delete:control_group'}>
          <SpaceBetween direction={'horizontal'} size={'xs'}>
            <Button
              variant={'normal'}
              formAction={'none'}
              // Hydration gate — see the toolbar danger action above.
              disabled={!controlGroup}
              onClick={() => {
                setIsDeleteModalVisible(true);
              }}
            >
              {st('delete_button', 'Delete')}
            </Button>
          </SpaceBetween>
        </Permission>
      }
    >
      <ControlledTabs
        activeTabId={activeTabId}
        tabs={tabs}
        variant={'container'}
        parentType={Parent_Type_Enum.ControlGroup}
        parent={controlGroup}
        disableSettings={objectToolbarEnabled}
      />
      <TabSettingsModal
        parentType={Parent_Type_Enum.ControlGroup}
        isVisible={showTabSettings}
        onDismiss={() => setShowTabSettings(false)}
        parent={controlGroup}
      />
      <DeleteModal
        loading={loading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => setIsDeleteModalVisible(false)}
      >
        {st('confirm_delete_message', 'Are you sure you want to delete?')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
